import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import { type AccountsState, getActiveAccount, type StoredAccount } from "@tendly/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  activeTabAccountId,
  clearTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { pruneAliases } from "@/lib/account-alias";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accounts" || e.key === null) {
        reload();
      }
    };
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  const active = (): StoredAccount | null => {
    const tabPin = activeTabAccountId();
    return resolveTabAccount(state(), tabPin);
  };
  const activeUserId = () => active()?.userId ?? null;

  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);

    // D1/D2: Preserve current route shape on account switch.
    // If the matched route has entity params ($), fall back to section ancestor.
    const currentMatches = router?.state?.matches;
    const currentMatch =
      currentMatches?.findLast?.((m) => Boolean(m.fullPath)) ??
      (currentMatches && currentMatches.length > 0 ? currentMatches.at(-1) : undefined);
    const fullPath = currentMatch?.fullPath;
    if (fullPath && fullPath.includes("$")) {
      const ancestor = fullPath.split("$")[0]?.replace(/\/+$/, "");
      await navigate({ to: ancestor || "/" });
    }
  };

  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    queryClient.clear();
    primeSessionForAccount(guest);
    if (guest) {
      writeTabAccountId(guest.userId);
    }
    // D5: Guest transitions cross a privilege boundary, safe landing on root is deliberate.
    await navigate({ to: "/" });
  };

  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeUserId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    pruneAliases(next.accounts.map((a) => a.userId));
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        writeTabAccountId(fallback.userId);
        await navigate({ to: "/" });
      } else {
        clearTabAccountId();
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    activeAccount: active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
